import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useNavigate, useParams } from "react-router-dom";
import DataTable from "@/components/DataTable";
import StatusCard from "@/components/StatusCard";
import { getTransactions } from "@/api/transactions";
import { getApplications } from "@/api/application";
import { getUserById, deleteUser } from "@/api/auth";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function UserList() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const columns = [
    { header: "ID", accessor: "id" },
    { header: "Ad Soyad", accessor: "name" },
    { header: "E-posta", accessor: "email" },
    { header: "Son İşlem", accessor: "lastTransaction" },
    { header: "İşlem Tarihi", accessor: "transactionDate" },
    {
      header: "İşlemler",
      accessor: "actions",
      cell: (row) => (
        <div className="flex justify-center gap-2">
          <Button variant="show" size="icon" onClick={() => handleView(row)} />
          <Button
            variant="trash"
            size="icon"
            onClick={() => handleDeleteClick(row)}
          />
        </div>
      ),
    },
  ];
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [appName, setAppName] = useState("");

  useEffect(() => {
    getTransactions().then((response) => {
      const txs = Array.isArray(response.data)
        ? response.data
        : response.data?.body?.result || response.data?.body || [];
      const filtered = txs.filter(
        (tx) => parseInt(tx.app_id) === parseInt(appId)
      );
      const userIds = [...new Set(filtered.map((tx) => parseInt(tx.user_id)))];
      Promise.all(
        userIds.map((id) =>
          getUserById(id).then((userRes) => ({
            id,
            first_name: userRes.data?.first_name || "",
            last_name: userRes.data?.last_name || "",
            email: userRes.data?.email || "",
            ...(() => {
              const userTxs = filtered.filter(
                (tx) => parseInt(tx.user_id) === id
              );
              const latest = userTxs.sort(
                (a, b) => new Date(b.date) - new Date(a.date)
              )[0];
              return {
                lastTransaction: latest?.process || "-",
                transactionDate: latest?.date || "-",
              };
            })(),
          }))
        )
      ).then((usersArr) => {
        setUsers(
          usersArr.map((u) => ({
            ...u,
            name: `${u.first_name} ${u.last_name}`.trim(),
          }))
        );
      });
    });
    getApplications().then((response) => {
      const appsArr = Array.isArray(response.data)
        ? response.data
        : response.data?.body?.result || response.data?.body || [];
      const app = appsArr.find((a) => a.id === parseInt(appId));
      setAppName(app?.name || "Uygulama");
    });
  }, [appId]);

  // Kullanıcı sil
  const handleDelete = () => {
    if (!selectedUser) return;
    deleteUser(selectedUser.id)
      .then(() => {
        setDeleteDialogOpen(false);
        setSelectedUser(null);
        return getTransactions();
      })
      .then((response) => {
        const txs = Array.isArray(response.data)
          ? response.data
          : response.data?.body?.result || response.data?.body || [];
        const filtered = txs.filter(
          (tx) => parseInt(tx.app_id) === parseInt(appId)
        );
        const userIds = [
          ...new Set(filtered.map((tx) => parseInt(tx.user_id))),
        ];
        return Promise.all(
          userIds.map((id) =>
            getUserById(id).then((userRes) => ({
              id,
              first_name: userRes.data?.first_name || "",
              last_name: userRes.data?.last_name || "",
              email: userRes.data?.email || "",
              ...(() => {
                const userTxs = filtered.filter(
                  (tx) => parseInt(tx.user_id) === id
                );
                const latest = userTxs.sort(
                  (a, b) => new Date(b.date) - new Date(a.date)
                )[0];
                return {
                  lastTransaction: latest?.process || "-",
                  transactionDate: latest?.date || "-",
                };
              })(),
            }))
          )
        );
      })
      .then((usersArr) => {
        setUsers(
          usersArr.map((u) => ({
            ...u,
            name: `${u.first_name} ${u.last_name}`.trim(),
          }))
        );
      });
  };

  const handleView = (user) => {
    navigate(
      `/applicationmanagement/${appId}/users/${user.id}/transactionhistory`
    );
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const totalUsers = users.length;
  const activeUsers = users.length;
  const adminUsers = 0;
  const activeRatio =
    totalUsers > 0 ? `%${Math.round((activeUsers / totalUsers) * 100)}` : "%0";
  const adminRatio =
    totalUsers > 0 ? `%${Math.round((adminUsers / totalUsers) * 100)}` : "%0";

  return (
    <div className="p-6 ml-8">
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="left-icon"
          size="icon"
          onClick={() => navigate("/applicationmanagement")}
        />
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">{appName} - Kullanıcı Listesi</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <StatusCard
          title="Toplam Kullanıcı"
          description="Sistemdeki toplam kullanıcı sayısı"
          additionalInfo={[
            { label: "Toplam Kullanıcı", value: totalUsers.toString() },
            {
              label: "Son Güncelleme",
              value: new Date().toLocaleDateString("tr-TR"),
            },
          ]}
        />
        <StatusCard
          title="Aktif Kullanıcı"
          description="Şu anda aktif olan kullanıcı sayısı"
          additionalInfo={[
            { label: "Aktif Kullanıcı", value: activeUsers.toString() },
            { label: "Aktiflik Oranı", value: activeRatio },
          ]}
        />
        <StatusCard
          title="Admin Sayısı"
          description="Sistemdeki toplam admin sayısı"
          additionalInfo={[
            { label: "Admin Sayısı", value: adminUsers.toString() },
            { label: "Admin Oranı", value: adminRatio },
          ]}
        />
      </div>

      <DataTable
        data={users}
        columns={columns}
        caption="Kullanıcı listesi"
        itemsPerPage={5}
        isAddButtonEnabled={false}
      />
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kullanıcıyı Sil</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedUser?.name} isimli kullanıcıyı silmek istediğinizden emin
              misiniz? Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
